/*
|‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾|
|                       - CUSTOM SLIDER -                       |
|_______________________________________________________________|
*/

(() => {
  if (window.__customSliderInit) return;
  window.__customSliderInit = true;

  const stateByCarousel = new WeakMap();

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const prefersReducedMotion = () =>
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

  function maxScrollLeft(track) {
    return Math.max(0, track.scrollWidth - track.clientWidth);
  }

  function getPageMetrics(track) {
    const styles = window.getComputedStyle(track);
    const paddingLeft = Number.parseFloat(styles.paddingLeft) || 0;
    const paddingRight = Number.parseFloat(styles.paddingRight) || 0;
    const pageWidth = Math.max(1, track.clientWidth - paddingLeft - paddingRight);

    return {
      paddingLeft,
      paddingRight,
      pageWidth,
      pageCenter: track.scrollLeft + paddingLeft + pageWidth / 2,
    };
  }

  function centeredIndex(track, slides) {
    const { pageCenter } = getPageMetrics(track);

    let best = 0;
    let bestDist = Infinity;

    slides.forEach((slide, index) => {
      const center = slide.offsetLeft + slide.offsetWidth / 2;
      const dist = Math.abs(center - pageCenter);

      if (dist < bestDist) {
        bestDist = dist;
        best = index;
      }
    });

    return best;
  }

  function targetLeftForSlide(track, slide, align = "start") {
    const max = maxScrollLeft(track);
    const { paddingLeft, pageWidth } = getPageMetrics(track);

    if (!slide) return track.scrollLeft;

    let target;

    if (align === "center") {
      target = slide.offsetLeft - paddingLeft - (pageWidth - slide.offsetWidth) / 2;
    } else if (align === "end") {
      target = slide.offsetLeft - paddingLeft - pageWidth + slide.offsetWidth;
    } else {
      target = slide.offsetLeft - paddingLeft;
    }

    return clamp(target, 0, max);
  }

  function scrollToSlide(track, slide, align = "start", behavior = "smooth") {
    if (!slide) return;

    track.scrollTo({
      left: targetLeftForSlide(track, slide, align),
      behavior: prefersReducedMotion() ? "auto" : behavior,
    });
  }

  function updateArrows(track, prevBtn, nextBtn) {
    const max = maxScrollLeft(track);
    const left = track.scrollLeft;

    prevBtn.disabled = left <= 2;
    nextBtn.disabled = left >= max - 2;
  }

  function getStep(carousel) {
    const section = carousel.closest(".custom-slider-section") || carousel;
    const raw = window.getComputedStyle(section).getPropertyValue("--cs-step").trim();
    const parsed = Number.parseInt(raw, 10);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  function setupDesktopMouseDrag(track, signal) {
    let pointerId = null;
    let startX = 0;
    let startLeft = 0;
    let dragging = false;
    let suppressNextClick = false;

    const stopDragging = () => {
      dragging = false;
      pointerId = null;
      track.classList.remove("cs-track--dragging");
    };

    track.addEventListener(
      "dragstart",
      (event) => {
        event.preventDefault();
      },
      { signal }
    );

    track.addEventListener(
      "click",
      (event) => {
        if (!suppressNextClick) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        suppressNextClick = false;
      },
      { capture: true, signal }
    );

    track.addEventListener(
      "pointerdown",
      (event) => {
        if (event.pointerType !== "mouse") return;
        if (event.button !== 0) return;

        pointerId = event.pointerId;
        startX = event.clientX;
        startLeft = track.scrollLeft;
        dragging = false;
        suppressNextClick = false;
      },
      { signal }
    );

    track.addEventListener(
      "pointermove",
      (event) => {
        if (event.pointerType !== "mouse") return;
        if (pointerId == null || event.pointerId !== pointerId) return;

        const delta = startX - event.clientX;

        if (!dragging && Math.abs(delta) < 6) return;

        if (!dragging) {
          dragging = true;
          suppressNextClick = true;
          track.classList.add("cs-track--dragging");

          try {
            track.setPointerCapture(event.pointerId);
          } catch (_) {}
        }

        event.preventDefault();
        track.scrollLeft = startLeft + delta;
      },
      { signal }
    );

    const endDrag = (event) => {
      if (pointerId == null || event.pointerId !== pointerId) return;

      const wasDragging = dragging;

      try {
        track.releasePointerCapture(event.pointerId);
      } catch (_) {}

      stopDragging();

      if (!wasDragging) {
        suppressNextClick = false;
        return;
      }

      window.setTimeout(() => {
        suppressNextClick = false;
      }, 0);
    };

    track.addEventListener("pointerup", endDrag, { signal });
    track.addEventListener("pointercancel", endDrag, { signal });
    track.addEventListener("lostpointercapture", () => {
      stopDragging();

      window.setTimeout(() => {
        suppressNextClick = false;
      }, 0);
    }, { signal });
  }

  function initCarousel(carousel) {
    if (!carousel) return;

    const section = carousel.closest(".custom-slider-section");

    if (section) {
      section.classList.add("custom-slider-loaded");
    }

    if (section?.dataset.csHasTitle === "1" && section.dataset.csTitleId) {
      carousel.setAttribute("aria-labelledby", section.dataset.csTitleId);
    }

    const track = carousel.querySelector("[data-custom-slider-track]");
    const prevBtn = carousel.querySelector("[data-custom-slider-prev]");
    const nextBtn = carousel.querySelector("[data-custom-slider-next]");

    if (!track || !prevBtn || !nextBtn) return;

    const slides = Array.from(carousel.querySelectorAll("[data-custom-slider-slide]"));

    if (slides.length <= 1) {
      prevBtn.disabled = true;
      nextBtn.disabled = true;
      return;
    }

    const prevState = stateByCarousel.get(carousel);

    if (prevState) {
      try {
        prevState.abort.abort();
      } catch (_) {}

      try {
        prevState.ro.disconnect();
      } catch (_) {}
    }

    const abort = new AbortController();
    const { signal } = abort;

    // Der Pfeilklick orientiert sich am aktuell mittig sichtbaren Produkt.
    // Die mittige Position wird in die erste Spalte der sichtbaren Seite
    // umgerechnet; danach geht es genau eine Seite weiter/zurueck.
    const currentPageStartIndex = () => {
      const step = getStep(carousel);
      const center = centeredIndex(track, slides);
      return clamp(center - Math.floor((step - 1) / 2), 0, slides.length - 1);
    };

    const onPrev = () => {
      const step = getStep(carousel);
      const { paddingLeft, pageWidth } = getPageMetrics(track);

      const visibleLeft = track.scrollLeft + paddingLeft;
      const visibleRight = visibleLeft + pageWidth;

      let firstSeenIndex = slides.findIndex((slide) => {
        const left = slide.offsetLeft;
        const right = left + slide.offsetWidth;

        const visibleWidth = Math.max(
          0,
          Math.min(right, visibleRight) - Math.max(left, visibleLeft)
        );

        return visibleWidth >= slide.offsetWidth * 0.8;
      });

      if (firstSeenIndex < 0) {
        firstSeenIndex = centeredIndex(track, slides);
      }

      const targetIndex = clamp(
        firstSeenIndex - step,
        0,
        slides.length - 1
      );

      scrollToSlide(track, slides[targetIndex], "start");
    };

    const onNext = () => {
      const { paddingRight } = getPageMetrics(track);
      const visibleRight = track.scrollLeft + track.clientWidth - paddingRight;

      let lastSeenIndex = -1;

      slides.forEach((slide, index) => {
        const threshold = slide.offsetLeft + slide.offsetWidth * 0.8;

        if (visibleRight >= threshold) {
          lastSeenIndex = index;
        }
      });

      const targetIndex = clamp(lastSeenIndex + 1, 0, slides.length - 1);
      scrollToSlide(track, slides[targetIndex], "start");
    };

    prevBtn.addEventListener("click", onPrev, { signal });
    nextBtn.addEventListener("click", onNext, { signal });

    let raf = 0;

    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);

      raf = requestAnimationFrame(() => {
        updateArrows(track, prevBtn, nextBtn);
      });
    };

    track.addEventListener("scroll", onScroll, { passive: true, signal });

    const ro = new ResizeObserver(() => {
      updateArrows(track, prevBtn, nextBtn);
    });

    ro.observe(track);

    setupDesktopMouseDrag(track, signal);

    stateByCarousel.set(carousel, {
      abort,
      ro,
    });

    updateArrows(track, prevBtn, nextBtn);
  }

  function initAllCustomSliders() {
    document
      .querySelectorAll(".custom-slider-section [data-custom-slider-carousel]")
      .forEach(initCarousel);
  }

  let dynamicSliderObserver = null;

  function startDynamicSliderObserver() {
    if (dynamicSliderObserver || !document.body) return;

    dynamicSliderObserver = new MutationObserver((mutations) => {
      const carousels = new Set();

      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;

          if (node.matches("[data-custom-slider-carousel]")) {
            carousels.add(node);
          }

          node
            .querySelectorAll("[data-custom-slider-carousel]")
            .forEach((carousel) => carousels.add(carousel));

          const parentCarousel = node.closest("[data-custom-slider-carousel]");

          if (parentCarousel) {
            carousels.add(parentCarousel);
          }
        }
      }

      if (carousels.size === 0) return;

      requestAnimationFrame(() => {
        carousels.forEach((carousel) => {
          if (!carousel.closest(".custom-slider-section")) return;

          initCarousel(carousel);
        });
      });
    });

    dynamicSliderObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function bootCustomSliders() {
    initAllCustomSliders();
    startDynamicSliderObserver();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootCustomSliders, {
      once: true,
    });
  } else {
    bootCustomSliders();
  }

  document.addEventListener("shopify:section:load", bootCustomSliders);
  document.addEventListener("custom-slider:contentchange", initAllCustomSliders);

  document.addEventListener("rebuy.ready", initAllCustomSliders);
  document.addEventListener("rebuy.productsChange", initAllCustomSliders);
})();
/*
|‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾|
|                    - RECENTLY VIEWED TRACKER -                 |
|________________________________________________________________|
*/

(() => {
  if (window.__csRecentlyViewedTracker) return;
  window.__csRecentlyViewedTracker = true;

  const STORAGE_KEY = "recently_viewed_products";
  const MAX_ITEMS = 20;
  const COOKIE_KEY = "recently_viewed_products_min";
  const COOKIE_MAX = 10;

  function safeJsonParse(str) {
    try {
      return JSON.parse(str);
    } catch (_) {
      return null;
    }
  }

  function imgWidth(src, width, fallback) {
    const s = src || fallback || "";

    if (!s) return "";

    try {
      const u = new URL(s, window.location.origin);
      u.searchParams.set("width", String(width));
      return u.toString();
    } catch (_) {
      return s.includes("?") ? `${s}&width=${width}` : `${s}?width=${width}`;
    }
  }

  function getMainVariantPicker() {
    return (
      document.querySelector('variant-picker[data-template-product-match="true"]') ||
      document.querySelector("variant-picker")
    );
  }

  function readSelectedVariantFromPicker() {
    const picker = getMainVariantPicker();

    if (!picker) return null;

    const jsonEl = picker.querySelector('script[type="application/json"]');

    if (!jsonEl?.textContent) return null;

    return safeJsonParse(jsonEl.textContent);
  }

  function getVariantIdFromUrl() {
    const v = new URL(window.location.href).searchParams.get("variant");
    return v ? String(v) : null;
  }

  function saveItem(item) {
    if (!item || !item.handle || !item.variant_id) return;

    try {
      const prev = safeJsonParse(localStorage.getItem(STORAGE_KEY)) || [];
      const next = Array.isArray(prev)
        ? prev.filter((x) => x && x.handle !== item.handle)
        : [];

      next.unshift(item);

      localStorage.setItem(STORAGE_KEY, JSON.stringify(next.slice(0, MAX_ITEMS)));
    } catch (_) {}

    try {
      const miniItem = { h: item.handle, v: item.variant_id };
      let mini = [];

      const m = document.cookie.match(
        `(^|;)\\s*${COOKIE_KEY}\\s*=\\s*([^;]+)`
      );

      if (m) {
        mini = safeJsonParse(decodeURIComponent(m[2])) || [];
      }

      mini = Array.isArray(mini)
        ? mini.filter((x) => x && x.h !== miniItem.h)
        : [];

      mini.unshift(miniItem);
      mini = mini.slice(0, COOKIE_MAX);

      document.cookie = `${COOKIE_KEY}=${encodeURIComponent(
        JSON.stringify(mini)
      )}; path=/; max-age=${60 * 60 * 24 * 7}`;
    } catch (_) {}
  }

  function initOnPdp() {
    const dataEl = document.querySelector("script[data-cs-rv-pdp]");

    if (!dataEl?.textContent) return;

    const data = safeJsonParse(dataEl.textContent);

    if (!data?.handle || !Array.isArray(data.variants)) return;

    const byId = Object.fromEntries(data.variants.map((v) => [String(v.id), v]));

    const comingSoonIds = new Set(
      Array.isArray(data.coming_soon_variant_ids)
        ? data.coming_soon_variant_ids.map((id) => String(id))
        : []
    );

    function buildData(variantId) {
      const v = byId[String(variantId)];

      if (!v) return null;

      const src =
        (v.featured_image && (v.featured_image.src || v.featured_image.url)) ||
        v.featured_image ||
        (v.image && (v.image.src || v.image)) ||
        null;

      const price = Number(v.price);
      const compare = Number(v.compare_at_price);
      const isComingSoon = comingSoonIds.has(String(v.id));

      return {
        handle: data.handle,
        title: data.title || "",
        subtitle: data.subtitle || "",
        variant_id: String(v.id),
        option1: v.option1 || "",
        option2: v.option2 || "",
        available: v.available !== false,
        coming_soon: isComingSoon,
        price: Number.isFinite(price) ? price : null,
        compare_at_price:
          Number.isFinite(compare) && Number.isFinite(price) && compare > price
            ? compare
            : null,
        image: imgWidth(src, 600, data.featured_image),
        ts: Date.now(),
      };
    }

    function sync() {
      const urlId = getVariantIdFromUrl();
      const pickerV = readSelectedVariantFromPicker();

      const id =
        urlId ||
        (pickerV?.id ? String(pickerV.id) : null) ||
        (data.variants[0] ? String(data.variants[0].id) : null);

      if (!id) return;

      const item = buildData(id);

      if (item) {
        saveItem(item);
      }
    }

    sync();

    document.addEventListener(
      "change",
      (e) => {
        const t = e.target;

        if (!(t instanceof Element)) return;

        if (t.closest("variant-picker")) {
          setTimeout(sync, 60);
        }
      },
      true
    );

    const hookHistory = (method) => {
      const original = history[method];

      if (typeof original !== "function") return;

      history[method] = function (...args) {
        const ret = original.apply(this, args);
        window.dispatchEvent(new Event("cs:locationchange"));
        return ret;
      };
    };

    if (!window.__csHistoryHooked) {
      window.__csHistoryHooked = true;
      hookHistory("pushState");
      hookHistory("replaceState");
    }

    window.addEventListener("popstate", sync);
    window.addEventListener("cs:locationchange", sync);
  }

  document.addEventListener("DOMContentLoaded", initOnPdp);
})();

/*
|‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾|
|                   - RECENTLY VIEWED RENDERER -                 |
|________________________________________________________________|
*/

(() => {
  if (window.__csRecentlyViewedRender) return;
  window.__csRecentlyViewedRender = true;

  function safeJsonParse(str) {
    try {
      return JSON.parse(str);
    } catch (_) {
      return null;
    }
  }

  function formatMoney(cents) {
    const n = Number(cents);

    if (!Number.isFinite(n)) return "";

    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      const fmt = window.Shopify.money_format || "{{amount}}";
      return window.Shopify.formatMoney(n, fmt);
    }

    const currency =
      window.Shopify?.currency?.active ||
      document.querySelector('meta[property="og:price:currency"]')?.content ||
      "EUR";

    const locale = document.documentElement.lang || "de-DE";

    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    }).format(n / 100);
  }

  function hydrateWrap(wrap) {
    const section = wrap.querySelector(".custom-slider-section");

    const key =
      section?.dataset.csRvKey ||
      wrap.dataset.csRvKey ||
      "recently_viewed_products";

    const max = Math.max(
      1,
      Number.parseInt(section?.dataset.csRvMax || wrap.dataset.csRvMax || "10", 10)
    );

    const isDesignMode =
      section?.dataset.csDesignMode === "1" || wrap.dataset.csDesignMode === "1";

    if (isDesignMode) {
      wrap.dataset.csDesignMode = "1";
    }

    let list = safeJsonParse(localStorage.getItem(key)) || [];

    if (!Array.isArray(list)) {
      list = [];
    }

    list = list.filter((x) => x && x.handle && x.title && x.image);
    list = list.slice(0, max);

    const track = wrap.querySelector("[data-cs-rv-track]");
    const tpl = wrap.querySelector("template[data-cs-rv-template]");
    const placeholder = wrap.querySelector("[data-cs-rv-empty]");

    if (!track || !tpl || !section) return;

    if (list.length === 0) {
      if (isDesignMode) {
        wrap.classList.add("custom-slider-ready");
        section.classList.add("custom-slider-loaded");

        if (placeholder) {
          placeholder.style.display = "block";
        }
      } else {
        wrap.classList.add("custom-slider-empty");
      }

      return;
    }

    if (placeholder) {
      placeholder.style.display = "none";
    }

    track.innerHTML = "";

    list.forEach((item, idx) => {
      const node = tpl.content.firstElementChild.cloneNode(true);

      const a = node.querySelector("a.cs-card");
      const img = node.querySelector("img.cs-image");
      const title = node.querySelector(".cs-product-title");
      const subtitle = node.querySelector(".cs-product-subtitle");
      const priceCur = node.querySelector(".cs-price-current");
      const priceCmp = node.querySelector(".cs-price-compare");
      const discountBadge = node.querySelector("[data-cs-rv-discount-badge]");
      const opt1 = node.querySelector(".cs-opt1");
      const opt2 = node.querySelector(".cs-opt2");
      const badges = node.querySelector("[data-cs-rv-badges]");
      const comingSoonBadge = node.querySelector("[data-cs-rv-badge-coming-soon]");
      const soldOutBadge = node.querySelector("[data-cs-rv-badge-sold-out]");
      const saleBadge = node.querySelector("[data-cs-rv-badge-sale]");
      const wishlistButton = node.querySelector("[data-custom-wishlist-button]");

      const href = `/products/${item.handle}${
        item.variant_id ? `?variant=${encodeURIComponent(item.variant_id)}` : ""
      }`;

      if (a) {
        a.href = href;
        a.setAttribute("aria-label", item.title || "");
      }

      if (img) {
        img.src = item.image;
        img.alt = item.title || "";
        img.loading = idx < 2 ? "eager" : "lazy";
        img.fetchPriority = idx < 2 ? "high" : "auto";
      }

      if (title) {
        title.textContent = item.title || "";
      }

      if (subtitle) {
        subtitle.textContent = item.subtitle || "";
      }

      if (priceCur) {
        priceCur.textContent = formatMoney(item.price);
      }

      if (priceCmp) {
        if (
          item.compare_at_price &&
          Number(item.compare_at_price) > Number(item.price)
        ) {
          priceCmp.style.display = "";
          priceCmp.textContent = formatMoney(item.compare_at_price);
        } else {
          priceCmp.style.display = "none";
          priceCmp.textContent = "";
        }
      }

      if (discountBadge) {
        const currentPrice = Number(item.price);
        const comparePrice = Number(item.compare_at_price);
        const percentage =
          Number.isFinite(currentPrice) &&
          Number.isFinite(comparePrice) &&
          comparePrice > currentPrice
            ? Math.floor(((comparePrice - currentPrice) * 100) / comparePrice)
            : 0;

        discountBadge.hidden = percentage <= 0;
        discountBadge.textContent = percentage > 0 ? `-${percentage}%` : "";
        if (percentage > 0) {
          discountBadge.setAttribute("aria-label", `${percentage} Prozent Rabatt`);
        } else {
          discountBadge.removeAttribute("aria-label");
        }
      }

      if (opt1) {
        opt1.textContent = item.option1 || "";
      }

      if (opt2) {
        opt2.textContent = item.option2 || "";
      }

      if (badges && comingSoonBadge && soldOutBadge && saleBadge) {
        const showBadges = badges.dataset.csRvShowBadges === "true";

        const isComingSoon =
          item.coming_soon === true ||
          item.coming_soon === "true" ||
          item.coming_soon === 1 ||
          item.coming_soon === "1";

        const isSoldOut = item.available === false && !isComingSoon;

        const isOnSale =
          item.compare_at_price &&
          Number(item.compare_at_price) > Number(item.price);

        badges.hidden = !(showBadges && (isComingSoon || isSoldOut || isOnSale));
        comingSoonBadge.hidden = !(showBadges && isComingSoon);
        soldOutBadge.hidden = !(showBadges && isSoldOut);
        saleBadge.hidden = !(showBadges && !isComingSoon && !isSoldOut && isOnSale);
      }

      if (wishlistButton) {
        wishlistButton.dataset.productHandle = item.handle || "";
        wishlistButton.dataset.variantId = item.variant_id || "";
      }

      track.appendChild(node);
    });

    wrap.classList.add("custom-slider-ready");
    wrap.classList.remove("custom-slider-empty");
    section.classList.add("custom-slider-loaded");

    document.dispatchEvent(new Event("custom-slider:contentchange"));
  }

  function hydrateAll() {
    document
      .querySelectorAll('.custom-slider-wrap[data-cs-feed-mode="recently_viewed"]')
      .forEach(hydrateWrap);
  }

  document.addEventListener("DOMContentLoaded", hydrateAll);
  document.addEventListener("shopify:section:load", hydrateAll);
})();

/*
|‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾|
|                 - SHOPIFY RECOMMENDATIONS FEED -               |
|________________________________________________________________|
*/

(() => {
  if (window.__customSliderRecommendationsFeedInit) return;
  window.__customSliderRecommendationsFeedInit = true;

  const observed = new WeakSet();

  function updateWrap(wrap) {
    const pr = wrap.querySelector("product-recommendations");

    if (!pr) return;

    if (pr.classList.contains("hidden")) {
      wrap.classList.add("custom-slider-empty");
      return;
    }

    const marker = pr.querySelector("[data-cs-has-items]");

    if (!marker) return;

    const has = marker.getAttribute("data-cs-has-items");

    if (has === "false") {
      wrap.classList.add("custom-slider-empty");
      return;
    }

    if (has === "true") {
      wrap.classList.remove("custom-slider-empty");
      wrap.querySelector(".custom-slider-section")?.classList.add("custom-slider-loaded");

      document.dispatchEvent(new Event("custom-slider:contentchange"));
    }
  }

  function observeWrap(wrap) {
    if (observed.has(wrap)) return;

    observed.add(wrap);

    const pr = wrap.querySelector("product-recommendations");

    if (!pr) return;

    const mo = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.target === pr)) {
        updateWrap(wrap);
      }
    });

    mo.observe(pr, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "data-recommendations-performed"],
    });

    updateWrap(wrap);
  }

  function init() {
    document
      .querySelectorAll('.custom-slider-wrap[data-cs-feed-mode="recommendations"]')
      .forEach(observeWrap);
  }

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("shopify:section:load", init);
})();